import { useSatuEventTracker } from 'utils/satuEvent'
import AndroidHandler from 'utils/androidHandler'
import { useAuth } from 'utils/auth'

import type {
  TSETrackEventDefaultAtributes,
  TSETrackSearchSubmitted,
  TSETrackAccordionClicked,
  TSETrackContentClicked,
} from 'types/kurikulum-merdeka'

const SATU_EVENT_NAME = 'guru_core_pwa_event_tracker_v1'
const TRACKER_EVENT_NAME = 'GURU_CORE_APP'
const TRACKER_SCREEN_NAME = 'TENTANG_KURIKULUM_MERDEKA'
const DEFAULT_TIMEOUT = 5000 // 5 seconds

type TUseSETrackEventProps = {
  appDeviceID: string
}
export const useSETrackEvent = ({ appDeviceID }: TUseSETrackEventProps) => {
  const hasAppDeviceID = Boolean(appDeviceID)
  const androidHandler = AndroidHandler()
  const { session } = useAuth()
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()

  const SETrackEventDefaultAtributes: TSETrackEventDefaultAtributes = {
    screen_name: TRACKER_SCREEN_NAME,
    is_login: [Boolean(session), 'bool'],
    is_webview: [androidHandler.enabled, 'bool'],
  }

  const SETrackSearchSubmitted = ({
    submittedInput,
  }: TSETrackSearchSubmitted): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: 'SEARCH_TENTANG_KURIKULUM_MERDEKA',
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: 'HOMEPAGE',
          submitted_input: submittedInput,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  const SETrackPageLoaded = (): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: 'LOAD_TENTANG_KURIKULUM_MERDEKA_PAGE',
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: 'HOMEPAGE',
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  const SETrackAccordionClicked = ({
    activityType,
    index,
    pageElement,
    action,
  }: TSETrackAccordionClicked): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: activityType,
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: 'TENTANG_KURIKULUM_MERDEKA',
          index: [index, 'int'],
          page_element: pageElement,
          action,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  const SETrackContentClicked = ({
    articleUrl,
  }: TSETrackContentClicked): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      sendUserData: true,
      eventParams: {
        activity_type: 'CLICK_TENTANG_KURIKULUM_MERDEKA_CONTENT',
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: 'TENTANG_KURIKULUM_MERDEKA',
          article_url: articleUrl,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  return {
    SETrackSearchSubmitted,
    SETrackPageLoaded,
    SETrackAccordionClicked,
    SETrackContentClicked,
  }
}
